/**
 * DockItem — macOS-style hover magnification
 * Right-click: Keep in Dock / Remove from Dock
 * Rule 1: MAX 200 lines
 */
import { motion } from 'framer-motion'
import { ExternalLink, Pin, PinOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { DockItemConfig } from './dockConfig'
import type { DockPosition } from './useDockPosition'

interface Props {
  item: DockItemConfig
  active: boolean
  isPinned: boolean
  isTemp?: boolean
  onClick: () => void
  onPin: () => void
  onUnpin: () => void
  dockPosition?: DockPosition
  baseSize?: number
}

const spring = { type: 'spring' as const, stiffness: 400, damping: 17 }

/** Fetch badge count for flzhub and task-manager dock items */
function useBadgeCount(itemId: string): number {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (itemId !== 'flzhub' && itemId !== 'task-manager') return

    let cancelled = false

    async function fetchCount() {
      try {
        if (itemId === 'flzhub') {
          const res = await fetch('/api/flzhub/unread-count')
          if (res.ok) {
            const data = await res.json() as { count?: number; unread?: number }
            if (!cancelled) setCount(data.count ?? data.unread ?? 0)
          } else {
            // Fallback: localStorage
            const stored = localStorage.getItem('flzhub:unread')
            if (!cancelled) setCount(stored ? parseInt(stored, 10) : 0)
          }
        } else if (itemId === 'task-manager') {
          const res = await fetch('/api/tasks?status=todo&limit=1')
          if (res.ok) {
            const data = await res.json() as { total?: number; count?: number; data?: unknown[] }
            if (!cancelled) setCount(data.total ?? data.count ?? (Array.isArray(data.data) ? data.data.length : 0))
          }
        }
      } catch {
        // silently ignore network errors
      }
    }

    fetchCount()
    const interval = setInterval(fetchCount, 30_000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [itemId])

  return Math.min(count, 99)
}

export default function DockItem({ item, active, isPinned, isTemp = false, onClick, onPin, onUnpin, dockPosition = 'bottom', baseSize = 46 }: Props) {
  const Icon = item.icon
  const [hovered, setHovered] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [ctxOpen, setCtxOpen] = useState(false)
  const [ctxPos, setCtxPos] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLDivElement>(null)
  const isHorizontal = dockPosition === 'bottom' || dockPosition === 'top'
  const badgeCount = useBadgeCount(item.id)

  const tooltipPos: React.CSSProperties =
    dockPosition === 'bottom' ? { bottom: '110%', left: '50%', transform: `translateX(-50%)` } :
    dockPosition === 'top'    ? { top: '110%',    left: '50%', transform: `translateX(-50%)` } :
    dockPosition === 'left'   ? { left: '110%',   top: '50%',  transform: `translateY(-50%)` } :
                                { right: '110%',  top: '50%',  transform: `translateY(-50%)` }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCtxPos({ x: e.clientX, y: e.clientY })
    setCtxOpen(true)
  }

  const closeCtx = () => setCtxOpen(false)

  return (
    <>
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        aria-label={`${item.label}${active ? ' (open)' : ''}${isTemp ? ' (temporary)' : ''}`}
        aria-pressed={active}
        style={{ display: 'flex', flexDirection: isHorizontal ? 'column' : 'row', alignItems: 'center', gap: 3, position: 'relative', cursor: 'pointer' }}
        onClick={onClick}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setPressed(false) }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
      >
        {/* Tooltip */}
        {item.label && (
          <div style={{
            position: 'absolute', pointerEvents: 'none', whiteSpace: 'nowrap',
            background: 'var(--os-surface)', backdropFilter: 'blur(12px)',
            border: '1px solid var(--os-border)', color: 'var(--os-text)',
            fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '7px',
            boxShadow: 'var(--os-card-shadow)',
            opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
            zIndex: 9999, ...tooltipPos,
          }}>
            {item.label}
            {isTemp && <span style={{ fontSize: 9, color: 'var(--os-muted)', marginLeft: 4 }}>•</span>}
          </div>
        )}

        {/* Icon */}
        <motion.div
          animate={pressed ? { scale: 0.9, y: 2 } : hovered ? { scale: 1.3, y: -8 } : { scale: 1, y: 0 }}
          transition={spring}
          style={{
            width: baseSize, height: baseSize, borderRadius: 12,
            background: active
              ? `${item.color}25`
              : isTemp
                ? `linear-gradient(135deg, ${item.color}88, ${item.color}55)`
                : `linear-gradient(135deg, ${item.color}cc, ${item.color}88)`,
            border: active ? `1.5px solid ${item.color}` : isTemp ? `1px dashed ${item.color}66` : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: active ? `0 0 12px ${item.color}40` : hovered ? `0 8px 24px ${item.color}60` : 'none',
            flexShrink: 0, willChange: 'transform',
            opacity: isTemp ? 0.75 : 1,
            position: 'relative',
          }}
        >
          <Icon size={Math.round(baseSize * 0.44)} color="#fff" />
          {/* Badge */}
          {badgeCount > 0 && (
            <div style={{
              position: 'absolute', top: -4, right: -4,
              minWidth: 16, height: 16, borderRadius: 8,
              background: '#ef4444', color: '#fff',
              fontSize: 9, fontWeight: 700, lineHeight: '16px',
              textAlign: 'center', padding: '0 3px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
            }}>
              {badgeCount}
            </div>
          )}
        </motion.div>

        {/* Active dot */}
        <div style={{ width: 4, height: 4, borderRadius: '50%', background: active ? item.color : 'transparent', transition: 'background 0.15s' }} />
      </div>

      {/* Context Menu */}
      {ctxOpen && (
        <>
          {/* Backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }} onClick={closeCtx} />
          <div style={{
            position: 'fixed', left: ctxPos.x, top: ctxPos.y, zIndex: 10001,
            background: 'var(--os-surface)', border: '1px solid var(--os-border)',
            borderRadius: 8, padding: '4px 0', minWidth: 180,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            {/* App name header */}
            <div style={{ padding: '6px 12px 4px', fontSize: 11, fontWeight: 700, color: 'var(--os-muted)', borderBottom: '1px solid var(--os-border)', marginBottom: 4 }}>
              {item.label}
            </div>

            <CtxBtn icon={<ExternalLink size={13} />} label="Open" onClick={() => { onClick(); closeCtx() }} />

            <div style={{ height: 1, background: 'var(--os-border)', margin: '3px 0' }} />

            {isPinned ? (
              <CtxBtn icon={<PinOff size={13} />} label="Remove from Dock" onClick={() => { onUnpin(); closeCtx() }} danger />
            ) : (
              <CtxBtn icon={<Pin size={13} />} label="Keep in Dock" onClick={() => { onPin(); closeCtx() }} />
            )}
          </div>
        </>
      )}
    </>
  )
}

function CtxBtn({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{ width: '100%', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: danger ? '#ef4444' : 'var(--os-text)', textAlign: 'left', fontFamily: 'inherit' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--os-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {icon}{label}
    </button>
  )
}
